import { Loader2 } from "lucide-react";
import XeroLastRunSummary from "@/components/admin/XeroLastRunSummary";
import XeroPanel from "@/components/admin/XeroPanel";
import { type MetricDefinition } from "@/components/admin/XeroMetricInfo";
import { type XeroPipelineRun } from "@/services/xeroApi";

const METRICS: MetricDefinition[] = [
  {
    term: "Organisations",
    definition:
      "Every organisation Xero currently grants access to, whether or not this run touched it. It can exceed the number that reported, which is the point: an organisation with no row is one the run never got to.",
  },
  {
    term: "Loaded",
    definition: "Organisations where at least one journal row landed.",
  },
  {
    term: "Reported nothing",
    definition:
      "Organisations that answered with no new journals above their watermark, plus any still waiting to start. Expected on a quiet day.",
  },
  {
    term: "Failed",
    definition:
      "Organisations that raised an error. The reason column separates a spent Xero call budget from a token problem from anything else.",
  },
  {
    term: "Not attempted",
    definition:
      "Organisations the run never reached, because an earlier one spent its daily Xero budget and the loop stopped. Nothing is lost — the next run resumes from each watermark.",
  },
  {
    term: "Rows",
    definition:
      "Journal rows written across the run. This counts rows merged, not rows added: re-loading an unchanged journal writes it again and is counted again, so it is not a measure of new data.",
  },
  {
    term: "Reason",
    definition:
      "Why one organisation ended the way it did, as the Fabric notebook reported it when closing the row. Runs recorded before this field existed show no reason.",
  },
];

interface Props {
  run?: XeroPipelineRun;
  isLoading: boolean;
  isFetching: boolean;
  hasNoRuns: boolean;
  onRefresh: () => void;
}

/** The most recent run: a summary line, with the per-organisation reasons
 *  behind a button rather than inline. */
export default function XeroLastRunPanel({
  run,
  isLoading,
  isFetching,
  hasNoRuns,
  onRefresh,
}: Props) {
  return (
    <XeroPanel
      title="Last run"
      blurb="The most recent pipeline execution, and why each organisation ended the way it did."
      metrics={METRICS}
      onRefresh={run ? onRefresh : undefined}
      isFetching={isFetching}
    >
      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading the latest run…
        </div>
      )}

      {run && <XeroLastRunSummary run={run} />}

      {!run && !isLoading && (
        <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          {hasNoRuns
            ? "No runs have been started from Prism yet. Runs started from the Fabric workspace report to Watermarks."
            : "No run to show."}
        </div>
      )}
    </XeroPanel>
  );
}
